import { defineMessages } from 'react-intl-next';

/** Shape of one keyword entry; spelled out so `isolatedDeclarations` is satisfied. */
type KeywordMessages<Key extends string> = Record<
	Key,
	{
		id: string;
		defaultMessage: string;
		description: string;
	}
>;

/**
 * Title keywords naming a status update outright. Defined via the standard i18n
 * package so they can localize per locale over time; the English seed
 * undercounts non-English users, a documented caveat.
 *
 * Each entry is a single keyword/phrase; the whole set is materialized into a
 * string list at match time (see `getTitleKeywords`). All matched whole-word,
 * case-insensitive.
 */
export const titleKeywordMessages: KeywordMessages<
	| 'statusUpdate'
	| 'statusReport'
	| 'projectUpdate'
	| 'progressUpdate'
	| 'teamUpdate'
	| 'weekInReview'
	| 'weeklyRecap'
> = defineMessages({
	statusUpdate: {
		id: 'status-update-signal.title-keyword.status-update',
		defaultMessage: 'status update',
		description: 'Title phrase indicating a page is a status update.',
	},
	statusReport: {
		id: 'status-update-signal.title-keyword.status-report',
		defaultMessage: 'status report',
		description: 'Title phrase indicating a page is a status report.',
	},
	projectUpdate: {
		id: 'status-update-signal.title-keyword.project-update',
		defaultMessage: 'project update',
		description: 'Title phrase indicating a page is a project update.',
	},
	progressUpdate: {
		id: 'status-update-signal.title-keyword.progress-update',
		defaultMessage: 'progress update',
		description: 'Title phrase indicating a page is a progress update.',
	},
	teamUpdate: {
		id: 'status-update-signal.title-keyword.team-update',
		defaultMessage: 'team update',
		description: 'Title phrase indicating a page is a team update.',
	},
	weekInReview: {
		id: 'status-update-signal.title-keyword.week-in-review',
		defaultMessage: 'week in review',
		description: 'Title phrase indicating a page reviews the past week.',
	},
	weeklyRecap: {
		id: 'status-update-signal.title-keyword.weekly-recap',
		defaultMessage: 'weekly recap',
		description: 'Title phrase indicating a page recaps the past week.',
	},
});

/**
 * Cadence signals in the title of the page's parent page or folder. When the
 * user is on child content, a parent/folder titled like a recurring series is
 * itself a status-update signal.
 */
export const parentKeywordMessages: KeywordMessages<
	'statusUpdates' | 'weeklyUpdates' | 'monthly' | 'updates' | 'weeklyRecaps' | 'monthlyRecaps'
> = defineMessages({
	statusUpdates: {
		id: 'status-update-signal.parent-keyword.status-updates',
		defaultMessage: 'status updates',
		description: 'Parent page or folder title phrase indicating a status update series.',
	},
	weeklyUpdates: {
		id: 'status-update-signal.parent-keyword.weekly-updates',
		defaultMessage: 'weekly updates',
		description: 'Parent page or folder title phrase indicating a weekly update series.',
	},
	monthly: {
		id: 'status-update-signal.parent-keyword.monthly',
		defaultMessage: 'monthly',
		description: 'Parent page or folder title keyword indicating a monthly series.',
	},
	updates: {
		id: 'status-update-signal.parent-keyword.updates',
		defaultMessage: 'updates',
		description: 'Parent page or folder title keyword indicating a series of updates.',
	},
	weeklyRecaps: {
		id: 'status-update-signal.parent-keyword.weekly-recaps',
		defaultMessage: 'weekly recaps',
		description: 'Parent page or folder title phrase indicating a weekly recap series.',
	},
	monthlyRecaps: {
		id: 'status-update-signal.parent-keyword.monthly-recaps',
		defaultMessage: 'monthly recaps',
		description: 'Parent page or folder title phrase indicating a monthly recap series.',
	},
});

/**
 * Recurrence indicators beyond the cadence adjectives — a bare "week" or an
 * "end of week" wrap-up. Combined (AND) with an update word to signal a
 * recurring status update (see `getStatusUpdateSignalMatch`). Localized month names are
 * added at match time (see `getRecurrenceKeywords`).
 */
export const recurrenceKeywordMessages: KeywordMessages<'week' | 'endOfWeek'> = defineMessages({
	week: {
		id: 'status-update-signal.recurrence-keyword.week',
		defaultMessage: 'week',
		description: 'Title keyword indicating a weekly recurrence (e.g. "Week 32").',
	},
	endOfWeek: {
		id: 'status-update-signal.recurrence-keyword.end-of-week',
		defaultMessage: 'end of week',
		description: 'Title phrase indicating an end-of-week wrap-up.',
	},
});

/**
 * Update words. Paired (AND) with a recurrence indicator, one of these in the
 * title marks a recurring status update (see `getStatusUpdateSignalMatch`).
 */
export const statusUpdateKeywordMessages: KeywordMessages<
	'update' | 'status' | 'recap' | 'report'
> = defineMessages({
	update: {
		id: 'status-update-signal.update-word.update',
		defaultMessage: 'update',
		description: 'Word indicating a status update.',
	},
	status: {
		id: 'status-update-signal.update-word.status',
		defaultMessage: 'status',
		description: 'Word indicating a status update.',
	},
	recap: {
		id: 'status-update-signal.update-word.recap',
		defaultMessage: 'recap',
		description: 'Word indicating a recap.',
	},
	report: {
		id: 'status-update-signal.update-word.report',
		defaultMessage: 'report',
		description: 'Word indicating a report.',
	},
});

/**
 * The single cadence adjective per cadence, matched whole-word and
 * case-insensitive (see `detectStatusUpdateCadence`). English seeds,
 * localizable over time. `biWeekly` is the hyphenated spelling of `biweekly`
 * and maps to the same cadence so a "bi-weekly" title is not mistaken for a
 * weekly one.
 */
export const cadenceKeywordMessages: KeywordMessages<
	'daily' | 'weekly' | 'biweekly' | 'biHypthenWeekly' | 'fortnightly' | 'monthly' | 'quarterly'
> = defineMessages({
	daily: {
		id: 'status-update-signal.cadence-keyword.daily',
		defaultMessage: 'daily',
		description: 'Keyword indicating a status update recurs daily.',
	},
	weekly: {
		id: 'status-update-signal.cadence-keyword.weekly',
		defaultMessage: 'weekly',
		description: 'Keyword indicating a status update recurs weekly.',
	},
	biweekly: {
		id: 'status-update-signal.cadence-keyword.biweekly',
		defaultMessage: 'biweekly',
		description: 'Keyword indicating a status update recurs every two weeks.',
	},
	biHypthenWeekly: {
		id: 'status-update-signal.cadence-keyword.bi-weekly',
		defaultMessage: 'bi-weekly',
		description: 'Hyphenated spelling of the biweekly cadence keyword.',
	},
	fortnightly: {
		id: 'status-update-signal.cadence-keyword.fortnightly',
		defaultMessage: 'fortnightly',
		description: 'Hyphenated spelling of the fortnightly cadence keyword.',
	},
	monthly: {
		id: 'status-update-signal.cadence-keyword.monthly',
		defaultMessage: 'monthly',
		description: 'Keyword indicating a status update recurs monthly.',
	},
	quarterly: {
		id: 'status-update-signal.cadence-keyword.quarterly',
		defaultMessage: 'quarterly',
		description: 'Keyword indicating a status update recurs quarterly.',
	},
});
